import { useMemo } from "react";
import routeList from "constants/routes";

const createPermissionMap = (permissionsArray) => {
  if (!Array.isArray(permissionsArray)) return {};
  return permissionsArray.reduce((acc, perm) => {
    if (perm.NomeInterno) {
      acc[perm.NomeInterno] = {
        name: perm.NomeMenu,
        id: perm.IdMenu,
        idPai: perm.IdCategoriaPai,
        fullData: perm,
      };
    }
    return acc;
  }, {});
};

const buildHierarchyTree = (itemsArray) => {
  if (!Array.isArray(itemsArray)) {
    return { tree: {}, itemsById: {} };
  }

  const tree = {};
  const itemsById = {};

  itemsArray.forEach((item) => {
    if (item.IdMenu !== undefined && item.IdMenu !== null) {
      const idMenu = String(item.IdMenu);
      itemsById[idMenu] = {
        ...item,
        children: [],
      };
    }
  });

  itemsArray.forEach((item) => {
    if (item.IdMenu !== undefined && item.IdMenu !== null) {
      const idMenu = String(item.IdMenu);
      const idPai =
        item.IdMenuPai !== undefined && item.IdMenuPai !== null
          ? String(item.IdMenuPai)
          : null;

      if (idPai && itemsById[idPai]) {
        itemsById[idPai].children.push(itemsById[idMenu]);
      } else if (!idPai || !itemsById[idPai]) {
        tree[idMenu] = itemsById[idMenu];
      }
    }
  });

  return { tree, itemsById };
};

const findFirstPermissionInBothMaps = (sessionMap, viewMap, keys) => {
  if (!keys) return undefined;
  const keysToCheck = Array.isArray(keys) ? keys : [keys];
  for (const key of keysToCheck) {
    if (sessionMap[key]) {
      return sessionMap[key];
    }
    if (viewMap[key]) {
      return viewMap[key];
    }
  }

  return undefined;
};

const hasPermissionInBothMaps = (sessionMap, viewMap, keys) => {
  if (!keys) return false;
  const keysToCheck = Array.isArray(keys) ? keys : [keys];
  for (const key of keysToCheck) {
    if (sessionMap[key] || viewMap[key]) {
      return true;
    }
  }

  return false;
};

const getDisplayNameForView = (view, userDetail) => {
  if (
    view.alternativeName &&
    view.userPermissionToAlternativeName &&
    !!userDetail?.UsuarioPerfis?.find(perfil => perfil.NomePerfil === view.userPermissionToAlternativeName)
  ) {
    return view.alternativeName;
  }
  return view.defaultName;
};

const sortViewsWithLeavesAlphabetically = (views) => {
  if (!Array.isArray(views) || views.length === 0) return views;
  const collapse = [];
  const leaves = [];
  views.forEach((v) => {
    if (v.collapse) collapse.push(v);
    else leaves.push(v);
  });
  leaves.sort((a, b) =>
    (a.name || "").localeCompare(b.name || "", "pt-BR", { sensitivity: "base" })
  );
  return [...collapse, ...leaves];
};

const sortViewsRecursive = (views) => {
  if (!Array.isArray(views) || views.length === 0) return views;
  const sorted = sortViewsWithLeavesAlphabetically(views);
  sorted.forEach((v) => {
    if (v.views?.length) v.views = sortViewsRecursive(v.views);
  });
  return sorted;
};

const mapChildrenRecursively = (
  childItem,
  sessionMap,
  viewMap,
  isVisionMode,
  depth = 0
) => {
  if (depth > 3) return null;

  if (!childItem.NomeInterno) return null;

  const hasPermission = hasPermissionInBothMaps(
    sessionMap,
    viewMap,
    childItem.NomeInterno
  );

  if (!hasPermission) return null;

  const childPermission = findFirstPermissionInBothMaps(
    sessionMap,
    viewMap,
    childItem.NomeInterno
  ) || {
    name: childItem.defaultName,
  };

  const childView = {
    name: childPermission.name || childItem.defaultName,
    permissionKey: childItem.NomeInterno,
    invisible: childItem.invisible !== undefined ? childItem.invisible : false,
    href: childItem.href,
    path: childItem.path,
  };

  if (childItem.children && childItem.children.length > 0) {
    childView.views = sortViewsRecursive(
      childItem.children
        .map((grandChild) =>
          mapChildrenRecursively(
            grandChild,
            sessionMap,
            viewMap,
            isVisionMode,
            depth + 1
          )
        )
        .filter((v) => v !== null)
    );

    childView.collapse = childView.views.length > 0;
  }

  return childView;
};

const mapChildrenToViews = (
  viewConfig,
  viewHierarchy,
  sessionMap,
  viewMap,
  isVisionMode,
  userDetail
) => {
  if (!viewConfig || !Array.isArray(viewConfig)) return [];

  return viewConfig
    .map((view) => {
      const displayName = getDisplayNameForView(view, userDetail);

      if (view.authRequired === false) {
        const mappedView = {
          ...view,
          name: displayName,
          invisible: view.invisible !== undefined ? view.invisible : false,
        };
        return mappedView;
      }

      if (isVisionMode && view.permissionKey === "TrocaUsuario") {
        return {
          ...view,
          name: displayName,
          invisible: view.invisible !== undefined ? view.invisible : false,
        };
      }

      if (!view.permissionKey) {
        return {
          ...view,
          name: displayName,
          invisible: view.invisible !== undefined ? view.invisible : false,
        };
      }

      const hasPermission = hasPermissionInBothMaps(
        sessionMap,
        viewMap,
        view.permissionKey
      );

      if (!hasPermission) return null;

      const viewPermission = findFirstPermissionInBothMaps(
        sessionMap,
        viewMap,
        view.permissionKey
      );

      const mappedView = {
        ...view,
        name: displayName,
        invisible: view.invisible !== undefined ? view.invisible : false,
      };

      if (
        viewPermission &&
        viewPermission.id !== undefined &&
        viewPermission.id !== null &&
        viewHierarchy.itemsById
      ) {
        const menuItemId = String(viewPermission.id);
        const menuItem = viewHierarchy.itemsById[menuItemId];

        if (menuItem && menuItem.children && menuItem.children.length > 0) {
          const validChildren = menuItem.children.filter((child) => {
            if (!child.NomeInterno) return false;
            return hasPermissionInBothMaps(
              sessionMap,
              viewMap,
              child.NomeInterno
            );
          });

          const childViews = validChildren
            .map((child) =>
              mapChildrenRecursively(child, sessionMap, viewMap, isVisionMode)
            )
            .filter((v) => v !== null);

          if (childViews.length > 0) {
            mappedView.views = sortViewsRecursive(childViews);
            mappedView.collapse = true;
          }
        } else if (
          view.views &&
          Array.isArray(view.views) &&
          view.views.length > 0
        ) {
          const validatedViews = mapChildrenToViews(
            view.views,
            viewHierarchy,
            sessionMap,
            viewMap,
            isVisionMode,
            userDetail
          );
          if (validatedViews.length > 0) {
            mappedView.views = sortViewsRecursive(validatedViews);
            mappedView.collapse = true;
          }
        }
      } else if (
        view.views &&
        Array.isArray(view.views) &&
        view.views.length > 0
      ) {
        const validatedViews = mapChildrenToViews(
          view.views,
          viewHierarchy,
          sessionMap,
          viewMap,
          isVisionMode,
          userDetail
        );
        if (validatedViews.length > 0) {
          mappedView.views = sortViewsRecursive(validatedViews);
          mappedView.collapse = true;
        }
      }

      return mappedView;
    })
    .filter((v) => v !== null);
};

const generateRoutes = (
  config,
  sessionMap,
  viewMap,
  sessionHierarchy,
  viewHierarchy,
  isVisionMode
) => {
  let userDetail = null;
  try {
    userDetail = JSON.parse(localStorage.getItem("userDetail") || "null");
  } catch {
    userDetail = null;
  }

  return config
    .map((route) => {
      if (route.authRequired === false) {
        let finalViews = route.views || [];

        if (route.collapse && Array.isArray(route.views)) {
          finalViews = sortViewsRecursive(
            mapChildrenToViews(
              route.views,
              viewHierarchy,
              sessionMap,
              viewMap,
              isVisionMode,
              userDetail
            )
          );
        }

        return {
          ...route,
          name: route.defaultName,
          views: finalViews,
          invisible: route.invisible !== undefined ? route.invisible : false,
        };
      }

      if (isVisionMode && route.permissionKey === "AdminPortal") {
        let finalViews = route.views || [];

        if (route.collapse && Array.isArray(route.views)) {
          finalViews = sortViewsRecursive(
            mapChildrenToViews(
              route.views,
              viewHierarchy,
              sessionMap,
              viewMap,
              isVisionMode,
              userDetail
            )
          );
        }

        return {
          ...route,
          name: route.defaultName,
          views: finalViews,
          invisible: route.invisible !== undefined ? route.invisible : false,
        };
      }

      const hasPermission = hasPermissionInBothMaps(
        sessionMap,
        viewMap,
        route.permissionKey
      );

      if (!hasPermission) return null;

      let finalViews = route.views || [];

      if (route.collapse && Array.isArray(route.views)) {
        finalViews = sortViewsRecursive(
          mapChildrenToViews(
            route.views,
            viewHierarchy,
            sessionMap,
            viewMap,
            isVisionMode,
            userDetail
          )
        );
      }

      return {
        ...route,
        name: route.defaultName,
        views: finalViews,
        invisible: route.invisible !== undefined ? route.invisible : false,
      };
    })
    .filter((v) => v !== null);
};

export const useRoutes = () => {
  const userVision = localStorage.getItem("userVision");

  const sessoesMenuUserRaw = localStorage.getItem("sessoesMenuUser");
  const viewsMenuUserRaw = localStorage.getItem("viewsMenuUser");
  const sessoesMenuUserVisionRaw = localStorage.getItem(
    "sessoesMenuUserVision"
  );
  const viewsMenuUserVisionRaw = localStorage.getItem("viewsMenuUserVision");

  const isVisionActive = useMemo(
    () => userVision && userVision !== "default",
    [userVision]
  );

  const permissionMaps = useMemo(() => {
    const isVisionActive = userVision && userVision !== "default";
    const sessoesRaw = isVisionActive
      ? sessoesMenuUserVisionRaw
      : sessoesMenuUserRaw;
    const viewsRaw = isVisionActive ? viewsMenuUserVisionRaw : viewsMenuUserRaw;

    try {
      const sessions = sessoesRaw ? JSON.parse(sessoesRaw) : null;
      const views = viewsRaw ? JSON.parse(viewsRaw) : null;
      return {
        sessionMap: createPermissionMap(sessions),
        viewMap: createPermissionMap(views),
        sessionHierarchy: buildHierarchyTree(sessions),
        viewHierarchy: buildHierarchyTree(views),
      };
    } catch (error) {
      return {
        sessionMap: {},
        viewMap: {},
        sessionHierarchy: { tree: {}, itemsById: {} },
        viewHierarchy: { tree: {}, itemsById: {} },
      };
    }
  }, [
    userVision,
    sessoesMenuUserRaw,
    viewsMenuUserRaw,
    sessoesMenuUserVisionRaw,
    viewsMenuUserVisionRaw,
  ]);

  const routesVision = useMemo(
    () =>
      generateRoutes(
        routeList,
        permissionMaps.sessionMap,
        permissionMaps.viewMap,
        permissionMaps.sessionHierarchy,
        permissionMaps.viewHierarchy,
        isVisionActive
      ),
    [permissionMaps, isVisionActive]
  );

  return {
    routesVision,
  };
};
